// GtCandidateEmails (C1-EMAIL) — the Emails tab on a candidate record.
// Every email we sent them and every reply, threaded, newest thread first,
// with Reply on each thread (answers in the same thread).
import { useState, useEffect, useCallback } from 'react';
import GtLoader from './GtLoader';
import GtEmailModal from './GtEmailModal';

const STATUS = {
  queued: ['gtem-pill--slate', 'Waiting'], sending: ['gtem-pill--slate', 'Sending'], sent: ['gtem-pill--slate', 'Sent'],
  delivered: ['gtem-pill--green', 'Delivered'], bounced: ['gtem-pill--rose', 'Bounced'], failed: ['gtem-pill--rose', 'Failed'],
};
const when = (v) => (v ? new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const frame = (html) => '<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>body{margin:0;padding:4px 2px;font:14px/1.6 Inter,system-ui,sans-serif;color:#161618}img{max-width:100%}blockquote{margin:8px 0;padding-left:10px;border-left:3px solid #E7E9ED;color:#646b76}</style></head><body>' + html + '</body></html>';
// The reply text without the quoted email underneath.
function freshText(t) {
  const lines = String(t || '').split('\n');
  const cut = lines.findIndex((l) => /^\s*>/.test(l) || /^On .+wrote:\s*$/.test(l.trim()) || /^-{2,}\s*Original Message/i.test(l));
  return (cut > 0 ? lines.slice(0, cut) : lines).join('\n').trim();
}

function Item({ it }) {
  const [open, setOpen] = useState(false);
  const mine = it.type === 'sent';
  const st = mine ? (it.last_event === 'complained' ? ['gtem-pill--rose', 'Marked as spam'] : STATUS[it.status] || ['gtem-pill--slate', it.status]) : null;
  return (
    <div className={'gtce-item' + (mine ? ' gtce-item--out' : ' gtce-item--in')}>
      <div className="gtce-ihead">
        <span className="gtce-who">{mine ? (it.sent_by_name || it.from_name || 'You') + ' to ' + it.to_email : 'They replied'}</span>
        {mine && it.status === 'queued' && it.scheduled_for ? <span className="gtem-pill gtem-pill--slate">Scheduled {when(it.scheduled_for)}</span> : st ? <span className={'gtem-pill ' + st[0]} title={it.error_message || ''}>{st[1]}</span> : null}
        {mine && it.send_total > 1 ? <span className="gtce-bulk" title={it.source_label || ''}>Sent to {it.send_total}{it.source_label ? ' from ' + it.source_label : ''}</span> : null}
        <span className="gtce-when">{when(it.at)}</span>
      </div>
      {mine ? (
        <>
          {open ? <iframe className="gtce-frame" title="Email" sandbox="allow-popups" srcDoc={frame(it.body_html || '')} />
            : <div className="gtce-snip">{(it.body_text ? String(it.body_text).split('\n--\n')[0] : String(it.body_html || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ')).replace(/\s+/g, ' ').trim().slice(0, 220)}</div>}
          <button type="button" className="gtsf-linkbtn gtce-more" onClick={() => setOpen((v) => !v)}>{open ? 'Show less' : 'Show the email'}</button>
        </>
      ) : (
        <>
          <div className="gtce-reply">{open ? (it.body_text || '') : (freshText(it.body_text) || '(No text)')}</div>
          {it.body_text && freshText(it.body_text) !== String(it.body_text).trim() && <button type="button" className="gtsf-linkbtn gtce-more" onClick={() => setOpen((v) => !v)}>{open ? 'Hide the quoted email' : 'Show the quoted email'}</button>}
        </>
      )}
    </div>
  );
}

export default function GtCandidateEmails({ candidate, authFetch, onChanged }) {
  const [threads, setThreads] = useState(null);
  const [error, setError] = useState(null);
  const [compose, setCompose] = useState(null);
  const load = useCallback(async () => {
    try { const r = await authFetch('/api/candidates/' + candidate.id + '/emails'); setThreads(r.threads || []); setError(null); }
    catch (e) { setError(e.message); setThreads([]); }
  }, [authFetch, candidate.id]);
  useEffect(() => { load(); }, [load]);

  const source = { kind: 'candidate', id: candidate.id, label: candidate.full_name || candidate.ref };
  const close = () => { setCompose(null); load(); if (onChanged) onChanged(); };

  return (
    <div className="gtce">
      <div className="gtce-top">
        <span className="gtce-count">{threads ? (threads.length ? threads.length + (threads.length === 1 ? ' conversation' : ' conversations') : 'No emails yet') : ''}</span>
        <button type="button" className="gtc-primary" disabled={!candidate.email} title={candidate.email ? '' : 'No email address on this record'} onClick={() => setCompose({})}>New email</button>
      </div>
      {error && <div className="gtnt-banner gtnt-banner--rose">{error}</div>}
      {threads === null && <GtLoader />}
      {threads && threads.length === 0 && !error && (
        <div className="gtr2-empty">{candidate.email ? 'Nothing sent yet. Emails you send and their replies show here.' : 'No email address on this record. Add one with Edit details.'}</div>
      )}
      {threads && threads.map((t, i) => {
        const lastReply = t.items.filter((x) => x.type === 'reply').slice(-1)[0];
        return (
          <div className="gtce-thread" key={t.id || i}>
            <div className="gtce-thead">
              <b>{t.subject || '(No subject)'}</b>
              <span>{t.items.length} {t.items.length === 1 ? 'email' : 'emails'}</span>
              <button type="button" className="gtc-btn-ghost gtce-replybtn" disabled={!candidate.email}
                onClick={() => setCompose({ replyTo: { id: lastReply ? lastReply.id : null, subject: t.subject, thread_id: t.id } })}>Reply</button>
            </div>
            {t.items.map((it) => <Item key={it.type + it.id} it={it} />)}
          </div>
        );
      })}
      {compose && (
        <GtEmailModal candidateIds={[candidate.id]} source={source} authFetch={authFetch} onClose={close}
          replyTo={compose.replyTo || null} />
      )}
    </div>
  );
}
